import { graphql } from 'react-relay';
import { useCallback } from 'react';

import { ConfluencePageAri, ConfluenceBlogpostAri } from '@atlassian/ari';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient';

import type {
	useUpdateContentPropertyPageCreateMutation$data,
	useUpdateContentPropertyPageCreateMutation$variables,
} from './__generated__/useUpdateContentPropertyPageCreateMutation.graphql';
import type {
	useUpdateContentPropertyBlogCreateMutation$data,
	useUpdateContentPropertyBlogCreateMutation$variables,
} from './__generated__/useUpdateContentPropertyBlogCreateMutation.graphql';
import type {
	useUpdateContentPropertyPageUpdateMutation$data,
	useUpdateContentPropertyPageUpdateMutation$variables,
} from './__generated__/useUpdateContentPropertyPageUpdateMutation.graphql';
import type {
	useUpdateContentPropertyPageDeleteMutation$data,
	useUpdateContentPropertyPageDeleteMutation$variables,
} from './__generated__/useUpdateContentPropertyPageDeleteMutation.graphql';
import type {
	useUpdateContentPropertyBlogUpdateMutation$data,
	useUpdateContentPropertyBlogUpdateMutation$variables,
} from './__generated__/useUpdateContentPropertyBlogUpdateMutation.graphql';
import type {
	useUpdateContentPropertyBlogDeleteMutation$data,
	useUpdateContentPropertyBlogDeleteMutation$variables,
} from './__generated__/useUpdateContentPropertyBlogDeleteMutation.graphql';

export const useUpdateContentProperty = ({
	contentId,
	contentType,
	options: queryOptions = {},
}: {
	contentId: string;
	contentType: string;
	options?: any;
}) => {
	const { cloudId } = useSessionData();

	const [createPagePropertyMutation, { loading: isCreatingPageProperty }] = useMutationInterop<
		useUpdateContentPropertyPageCreateMutation$data,
		useUpdateContentPropertyPageCreateMutation$variables
	>(
		graphql`
			mutation useUpdateContentPropertyPageCreateMutation(
				$propertyKey: String!
				$pageId: ID!
				$propertyValue: String!
			) {
				confluence {
					createPageProperty(input: { key: $propertyKey, pageId: $pageId, value: $propertyValue }) {
						success
						pageProperty {
							key
							value
						}
						errors {
							message
						}
					}
				}
			}
		`,
		{
			...queryOptions,
			client: getAGGClient(),
			context: {
				headers: {
					'X-ExperimentalApi': 'confluence-agg-beta',
				},
			},
		},
	);

	const [updatePagePropertyMutation, { loading: isUpdatingPageProperty }] = useMutationInterop<
		useUpdateContentPropertyPageUpdateMutation$data,
		useUpdateContentPropertyPageUpdateMutation$variables
	>(
		graphql`
			mutation useUpdateContentPropertyPageUpdateMutation(
				$propertyKey: String!
				$pageId: ID!
				$propertyValue: String!
			) {
				confluence {
					updateValuePageProperty(
						input: { key: $propertyKey, pageId: $pageId, value: $propertyValue }
					) {
						success
						pageProperty {
							key
							value
						}
						errors {
							message
						}
					}
				}
			}
		`,
		{
			...queryOptions,
			client: getAGGClient(),
			context: {
				headers: {
					'X-ExperimentalApi': 'confluence-agg-beta',
				},
			},
		},
	);

	const [deletePagePropertyMutation, { loading: isDeletingPageProperty }] = useMutationInterop<
		useUpdateContentPropertyPageDeleteMutation$data,
		useUpdateContentPropertyPageDeleteMutation$variables
	>(
		graphql`
			mutation useUpdateContentPropertyPageDeleteMutation($propertyKey: String!, $pageId: ID!) {
				confluence {
					deletePageProperty(input: { key: $propertyKey, pageId: $pageId }) {
						success
						errors {
							message
						}
					}
				}
			}
		`,
		{
			...queryOptions,
			client: getAGGClient(),
			context: {
				headers: {
					'X-ExperimentalApi': 'confluence-agg-beta',
				},
			},
		},
	);

	const [createBlogPropertyMutation, { loading: isCreatingBlogProperty }] = useMutationInterop<
		useUpdateContentPropertyBlogCreateMutation$data,
		useUpdateContentPropertyBlogCreateMutation$variables
	>(
		graphql`
			mutation useUpdateContentPropertyBlogCreateMutation(
				$propertyKey: String!
				$blogPostId: ID!
				$propertyValue: String!
			) {
				confluence {
					createBlogPostProperty(
						input: { key: $propertyKey, blogPostId: $blogPostId, value: $propertyValue }
					) {
						success
						blogPostProperty {
							key
							value
						}
						errors {
							message
						}
					}
				}
			}
		`,
		{
			...queryOptions,
			client: getAGGClient(),
			context: {
				headers: {
					'X-ExperimentalApi': 'confluence-agg-beta',
				},
			},
		},
	);

	const [updateBlogPropertyMutation, { loading: isUpdatingBlogProperty }] = useMutationInterop<
		useUpdateContentPropertyBlogUpdateMutation$data,
		useUpdateContentPropertyBlogUpdateMutation$variables
	>(
		graphql`
			mutation useUpdateContentPropertyBlogUpdateMutation(
				$propertyKey: String!
				$blogPostId: ID!
				$propertyValue: String!
			) {
				confluence {
					updateValueBlogPostProperty(
						input: { key: $propertyKey, blogPostId: $blogPostId, value: $propertyValue }
					) {
						success
						blogPostProperty {
							key
							value
						}
						errors {
							message
						}
					}
				}
			}
		`,
		{
			...queryOptions,
			client: getAGGClient(),
			context: {
				headers: {
					'X-ExperimentalApi': 'confluence-agg-beta',
				},
			},
		},
	);

	const [deleteBlogPropertyMutation, { loading: isDeletingBlogProperty }] = useMutationInterop<
		useUpdateContentPropertyBlogDeleteMutation$data,
		useUpdateContentPropertyBlogDeleteMutation$variables
	>(
		graphql`
			mutation useUpdateContentPropertyBlogDeleteMutation($propertyKey: String!, $blogPostId: ID!) {
				confluence {
					deleteBlogPostProperty(input: { key: $propertyKey, blogPostId: $blogPostId }) {
						success
						errors {
							message
						}
					}
				}
			}
		`,
		{
			...queryOptions,
			client: getAGGClient(),
			context: {
				headers: {
					'X-ExperimentalApi': 'confluence-agg-beta',
				},
			},
		},
	);

	const updateContentProperty = useCallback(
		({
			propertyKey,
			propertyValue: propertyValueString,
			isDelete = false,
			hasExistingProperty = false,
			options = {},
			shouldOptimisticResponse = false,
		}: {
			propertyKey: string;
			propertyValue?: string;
			isDelete?: boolean;
			hasExistingProperty?: boolean;
			options?: any;
			shouldOptimisticResponse?: boolean;
		}) => {
			if (isDelete) {
				if (contentType === 'page') {
					return deletePagePropertyMutation({
						variables: {
							pageId: ConfluencePageAri.create({
								siteId: cloudId,
								pageId: contentId,
							}).toString(),
							propertyKey,
						},
						...options,
						optimisticResponse: shouldOptimisticResponse
							? {
									success: true,
									errors: [],
								}
							: undefined,
					});
				} else if (contentType === 'blogpost') {
					return deleteBlogPropertyMutation({
						variables: {
							blogPostId: ConfluenceBlogpostAri.create({
								siteId: cloudId,
								blogpostId: contentId,
							}).toString(),
							propertyKey,
						},
						...options,
						optimisticResponse: shouldOptimisticResponse
							? {
									success: true,
									errors: [],
								}
							: undefined,
					});
				}
			} else {
				if (!!propertyValueString) {
					// Property value need to be stringified again before passing to graphql otherwise backend cannot parse it correctly
					const propertyValue = JSON.stringify(propertyValueString);
					if (hasExistingProperty) {
						// When there is existing property, we update it
						if (contentType === 'page') {
							return updatePagePropertyMutation({
								variables: {
									pageId: ConfluencePageAri.create({
										siteId: cloudId,
										pageId: contentId,
									}).toString(),
									propertyKey,
									propertyValue,
								},
								...options,
								optimisticResponse: shouldOptimisticResponse
									? {
											success: true,
											pageProperty: {
												key: propertyKey,
												value: propertyValue,
											},
											errors: [],
										}
									: undefined,
							});
						} else if (contentType === 'blogpost') {
							return updateBlogPropertyMutation({
								variables: {
									blogPostId: ConfluenceBlogpostAri.create({
										siteId: cloudId,
										blogpostId: contentId,
									}).toString(),
									propertyKey,
									propertyValue,
								},
								...options,
								optimisticResponse: shouldOptimisticResponse
									? {
											success: true,
											blogPostProperty: {
												key: propertyKey,
												value: propertyValue,
											},
											errors: [],
										}
									: undefined,
							});
						}
					} else {
						// When there is no existing property, we create it
						if (contentType === 'page') {
							return createPagePropertyMutation({
								variables: {
									pageId: ConfluencePageAri.create({
										siteId: cloudId,
										pageId: contentId,
									}).toString(),
									propertyKey,
									propertyValue,
								},
								...options,
								optimisticResponse: shouldOptimisticResponse
									? {
											success: true,
											pageProperty: {
												key: propertyKey,
												value: propertyValue,
											},
											errors: [],
										}
									: undefined,
							});
						} else if (contentType === 'blogpost') {
							return createBlogPropertyMutation({
								variables: {
									blogPostId: ConfluenceBlogpostAri.create({
										siteId: cloudId,
										blogpostId: contentId,
									}).toString(),
									propertyKey,
									propertyValue,
								},
								...options,
								optimisticResponse: shouldOptimisticResponse
									? {
											success: true,
											blogPostProperty: {
												key: propertyKey,
												value: propertyValue,
											},
											errors: [],
										}
									: undefined,
							});
						}
					}
				}
			}
		},
		[
			createPagePropertyMutation,
			createBlogPropertyMutation,
			updatePagePropertyMutation,
			updateBlogPropertyMutation,
			deletePagePropertyMutation,
			deleteBlogPropertyMutation,
			contentId,
			cloudId,
			contentType,
		],
	);

	const processUpdateContentPropertyResult = useCallback(
		(
			result:
				| useUpdateContentPropertyPageCreateMutation$data
				| useUpdateContentPropertyBlogCreateMutation$data
				| useUpdateContentPropertyPageUpdateMutation$data
				| useUpdateContentPropertyBlogUpdateMutation$data
				| useUpdateContentPropertyPageDeleteMutation$data
				| useUpdateContentPropertyBlogDeleteMutation$data
				| undefined,
		): {
			success?: boolean | null;
			key?: string | null;
			value?: string | null;
			errors?: Array<{ message?: string | null }> | null;
		} | null => {
			if (result && 'confluence' in result && result.confluence) {
				if ('createPageProperty' in result.confluence) {
					const { createPageProperty } = result.confluence;
					return {
						success: createPageProperty?.success,
						key: createPageProperty?.pageProperty?.key,
						value: createPageProperty?.pageProperty?.value,
						errors: createPageProperty?.errors ? [...createPageProperty.errors] : undefined,
					};
				}
				if ('createBlogPostProperty' in result.confluence) {
					const { createBlogPostProperty } = result.confluence;
					return {
						success: createBlogPostProperty?.success,
						key: createBlogPostProperty?.blogPostProperty?.key,
						value: createBlogPostProperty?.blogPostProperty?.value,
						errors: createBlogPostProperty?.errors ? [...createBlogPostProperty.errors] : undefined,
					};
				}
				if ('updateValuePageProperty' in result.confluence) {
					const { updateValuePageProperty } = result.confluence;
					return {
						success: updateValuePageProperty?.success,
						key: updateValuePageProperty?.pageProperty?.key,
						value: updateValuePageProperty?.pageProperty?.value,
						errors: updateValuePageProperty?.errors
							? [...updateValuePageProperty.errors]
							: undefined,
					};
				}
				if ('updateValueBlogPostProperty' in result.confluence) {
					const { updateValueBlogPostProperty } = result.confluence;
					return {
						success: updateValueBlogPostProperty?.success,
						key: updateValueBlogPostProperty?.blogPostProperty?.key,
						value: updateValueBlogPostProperty?.blogPostProperty?.value,
						errors: updateValueBlogPostProperty?.errors
							? [...updateValueBlogPostProperty.errors]
							: undefined,
					};
				}
				if ('deletePageProperty' in result.confluence) {
					const { deletePageProperty } = result.confluence;
					return {
						success: deletePageProperty?.success,
						errors: deletePageProperty?.errors ? [...deletePageProperty.errors] : undefined,
					};
				}
				if ('deleteBlogPostProperty' in result.confluence) {
					const { deleteBlogPostProperty } = result.confluence;
					return {
						success: deleteBlogPostProperty?.success,
						errors: deleteBlogPostProperty?.errors ? [...deleteBlogPostProperty.errors] : undefined,
					};
				}
			}
			return null;
		},
		[],
	);

	return {
		updateContentProperty,
		processUpdateContentPropertyResult,
		loading:
			isCreatingPageProperty ||
			isUpdatingPageProperty ||
			isDeletingPageProperty ||
			isCreatingBlogProperty ||
			isUpdatingBlogProperty ||
			isDeletingBlogProperty,
	};
};
